<svelte:window on:popstate={popState} />

{#if !$topicsData.loading}
    <Tabs tabs={selfPacedTabs} bind:selectedIndex={selectedTab} on:tabChanged={tabChangedNotification}>
        {#if selectedTab === plannerSelfPacedTabIds.all || plannerTabIds.all}
            <h2 class="screen-reader">{selfPacedTabs[1].title}</h2>
        {:else if selectedTab === plannerSelfPacedTabIds.topics || plannerTabIds.topics}
            <h2 class="screen-reader">{selfPacedTabs[0].title}</h2>
        {/if}
        <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
        <section
            id="panel-0"
            aria-labelledby="tab-topics"
            role="tabpanel"
            class={contentClass}
            tabindex="-1"
        >
            <Topics
                data={$topicsData}
                {selectedTab}
                on:endOfActionOnSelectedTopics={afterActionSelfPaced}
                on:createTopic={afterActionSelfPaced}
            />
        </section>

        {#if selectedTab !== plannerSelfPacedTabIds.topics && selectedTab !== plannerTabIds.topics}
            <div
                class={`its-plans-view-switch ${
                    showEmptyState
                        ? "its-plans-view-switch-position-empty"
                        : "its-plans-view-switch-position-list"
                }`}
            >
                {#if !showEmptyState}
                    <PlansToolbar
                        data={tabData}
                        selectedView={getPlannerView(selectedTab)}
                        {selectedPlanIds}
                        on:endOfActionOnSelectedPlans={afterActionSelfPaced}
                    />
                {/if}
                <TableViewLink
                    {config}
                    {i18n}
                    isDataLoading={tabData.loading}
                    backExtraParams={{
                        selectedTab
                    }}
                />
            </div>
        {/if}
        <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
        <section
            id="panel-1"
            aria-labelledby="tab-all"
            role="tabpanel"
            class={contentClass}
            tabindex="-1"
        >
            {#if showEmptyState && !showEmptyStateInformative}
                <EmptyState
                    {iconName}
                    title={titleEmptyTab}
                    description={i18n.emptyState_WithoutDateTab_Description}
                    showLink={config.canEdit}
                    linkType="primary-standalone"
                    linkText={i18n.addPlan}
                    href={createNewPlanUrl}
                    on:click={createNewPlan}
                />
                {#if config.canEdit && config.isImportEnabled}
                    <UseReadyMadeContentLink
                        href={`/Courses/${
                            config.courseId
                        }/ImportContent?importTopic=false&fromOverview=false&returnUrl=${encodeURI(
                            window.location.href
                        )}`}
                        linktext={i18n.useReadyMadeContent}
                        emptyState={true}
                    ></UseReadyMadeContentLink>
                {/if}
            {:else if showEmptyStateInformative}
                    <EmptyStateInformativeCard
                        backLink={backLinkDestinations.all}
                        fromOverview={false}
                    />
            {:else}
                <PlansView
                    data={$allPlansData}
                    {selectedTab}
                    loadMethod={loadAllPlans}
                    {selectedPlanIds}
                    {columnVisibilities}
                    {useGenericResourceColumn}
                    on:endOfActionOnSelectedPlans={afterActionSelfPaced}
                    on:planCompletionUpdate={() => dispatch(PLAN_COMPLETION_UPDATE_EVENT)}
                />
            {/if}
        </section>
    </Tabs>
{/if}

<script>
    import { onMount, createEventDispatcher } from "svelte";

    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";
    import Tabs from "@itslearning/prometheus/assets/navigation/Tabs/v1/Tabs.svelte";

    import PlansView from "@its-ui/planner-common/components/PlansView/PlansView.svelte";
    import Topics from "../Topics/Topics.svelte";
    import PlansToolbar from "@its-ui/planner-common/components/PlansToolbar/PlansToolbar.svelte";
    import { addBackDestinationParam } from "@its-ui/planner-common/helpers/backLinkHelper";
    import config from "@its-ui/planner-common/config";
    import i18n from "@its-ui/planner-common/i18n";
    import { createPlan, createPlanV2 } from "@its-ui/planner-common/api";
    import {
        topicsData,
        allPlansData,
        selectedPlanIds,
        allPlanCountsData,
        selectedTopicIds
    } from "@its-ui/planner-common/logic/plans/stores";
    import {
        loadTopics,
        loadAllPlans,
        loadAllPlanCounts
    } from "@its-ui/planner-common/logic/plans/loadDataController";
    import { getPlannerView } from "@its-ui/planner-common/helpers/converters";
    import { backLinkDestinations, plannerTabIds, plannerSelfPacedTabIds, PLAN_COMPLETION_UPDATE_EVENT } from "@its-ui/planner-common/constants";
    import { get } from "svelte/store";
    import TableViewLink from "@its-ui/planner-common/components/TableViewLink/TableViewLink.svelte";
    import UseReadyMadeContentLink from "@its-ui/planner-common/components/UseReadyMadeContentLink/UseReadyMadeContentLink.svelte";
    import EmptyStateInformativeCard from "@its-ui/planner-common/components/EmptyStateInformativeCard/EmptyStateInformativeCard.svelte";

    onMount(() => {
        loadAllPlanCounts(config.publicAccessKey);
        loadTopics(config.publicAccessKey);
    });

    const dispatch = createEventDispatcher();

    export let allCounts;
    export let selectedTab;

    /** Empty state variables **/
    const iconName = "plans";
    const titleEmptyTab = config.canEdit
        ? i18n.emptyState_Tab_Title_Teacher
        : i18n.emptyState_Title_Student;

    let showEmptyState = false;
    let showEmptyStateInformative = false;

    let tabData = {};
    let hasSelfPacedPlansLoaded = false;
    let newPlanUpdatePerformed = false;
    let lastSelectedSelfPacedTab = selectedTab;
    let createNewPlanUrl = "#";

    const contentClass = "prom-tabs__content";
    const columnVisibilities = config.columnVisibilities;
    const maxCountTreshold = 100;
    const maxCountDisplayText = " (99+)";
    const selfPacedTabs = [
        {
            id: "tab-topics",
            route: "topics",
            target: "#panel-0",
            title: config.topicName,
            loadMethod: loadTopics
        },
        {
            id: "tab-all",
            route: "all",
            target: "#panel-1",
            title: i18n.allPlans,
            loadMethod: loadAllPlans
        }
    ];
    const useGenericResourceColumn = config.useGenericResourceColumn;

    $: if (!$topicsData.loading) {
        if (lastSelectedSelfPacedTab === undefined) {
            const currentUrl = window.location.href.toLowerCase();
            const indexOfAllPlansTab = currentUrl.indexOf("all");
            const indexOfTopicsTab = currentUrl.indexOf("topics");
            const indexOfCurrent = currentUrl.indexOf("current");
            const indexOfChildId = currentUrl.indexOf("childid");

            if (config.publicAccessKey) {
                if (indexOfTopicsTab !== -1 && indexOfChildId === -1) {
                    // Navigate back to "Topics" tab
                    selectedTab = plannerSelfPacedTabIds.topics;
                } else if (
                    (indexOfCurrent !== -1 || indexOfAllPlansTab !== -1) &&
                    indexOfChildId === -1
                ) {
                    // Navigate back to "All plans" tab
                    selectedTab = plannerSelfPacedTabIds.all;
                } else {
                    if (indexOfTopicsTab !== -1) {
                        // Navigate back to "Topics" tab if there are topics
                        if($topicsData.topics.length !== 0) {
                            selectedTab = plannerSelfPacedTabIds.topics;
                        }
                        else {
                            selectedTab = plannerSelfPacedTabIds.all;
                        }
                    } else if (
                        $allPlanCountsData.topicsCount === "0" &&
                        $allPlanCountsData.allPlansCount !== "0"
                    ) {
                        selectedTab = plannerSelfPacedTabIds.all;
                    } else {
                        selectedTab = plannerSelfPacedTabIds.topics;
                    }
                }
            } else {
                if ($topicsData.topics.length === 0 && indexOfAllPlansTab === indexOfTopicsTab) {
                    selectedTab = plannerSelfPacedTabIds.all;
                } else {
                    selectedTab =
                        indexOfAllPlansTab !== -1
                            ? plannerSelfPacedTabIds.all
                            : plannerSelfPacedTabIds.topics;
                }
            }
        }
    }

    if (lastSelectedSelfPacedTab === plannerTabIds.topics) {
        lastSelectedSelfPacedTab = plannerSelfPacedTabIds.topics;
    }

    if (lastSelectedSelfPacedTab === plannerTabIds.all) {
        lastSelectedSelfPacedTab = plannerSelfPacedTabIds.all;
    }

    $: {
        allCounts = $allPlanCountsData;

        if (!allCounts.loading) {
            addCountsToTabTitles();
        }
    }

    $: {
        switch (selectedTab) {
            case plannerSelfPacedTabIds.topics:
                showEmptyState = !$topicsData.loading && $topicsData.topics.length === 0;
                tabData = $topicsData;
                break;
            case plannerSelfPacedTabIds.all:
                showEmptyState = !$allPlansData.loading && $allPlansData.plans.length === 0;
                showEmptyStateInformative = config.canEdit && config.isCourseTypeSelfPaced && showEmptyState;
                tabData = $allPlansData;
                break;

            default:
                break;
        }
    }

    $: {
        if (selectedTab === plannerSelfPacedTabIds.all || selectedTab === plannerTabIds.all) {
            if (selfPacedTabs[1].loadMethod && !hasSelfPacedPlansLoaded) {
                selfPacedTabs[1].loadMethod(config.publicAccessKey);
                hasSelfPacedPlansLoaded = true;
            }
        }
    }

    $: {
        const selectedPlans = get(selectedPlanIds);
        const selectedTopics = get(selectedTopicIds);

        if (
            (selectedPlans && selectedPlans.size > 0) ||
            (selectedTopics && selectedTopics.size > 0)
        ) {
            resetLoadStatusOfSelectedTab();
        }

        lastSelectedSelfPacedTab = selectedTab;

        let createdPlanIdParamString = "";

        if (!newPlanUpdatePerformed) {
            newPlanUpdatePerformed = true;
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const planIdParam = urlParams.get("createdPlanId");

            createdPlanIdParamString = planIdParam ? `?createdPlanId=${planIdParam}` : "";
        }

        if (selectedTab === plannerSelfPacedTabIds.all || selectedTab === plannerTabIds.all) {
            selectedTab = plannerSelfPacedTabIds.all;
            history.pushState(
                { tab: selectedTab },
                "",
                config.publicAccessKey
                    ? `/plans/public/${config.publicAccessKey}/${selfPacedTabs[1].route}/`
                    : `/plans/courses/${config.courseId}/${selfPacedTabs[1].route}/${createdPlanIdParamString}`
            );
        } else if (
            selectedTab === plannerSelfPacedTabIds.topics ||
            selectedTab === plannerTabIds.topics
        ) {
            selectedTab = plannerSelfPacedTabIds.topics;
            history.pushState(
                { tab: selectedTab },
                "",
                config.publicAccessKey
                    ? `/plans/public/${config.publicAccessKey}/${selfPacedTabs[0].route}/`
                    : `/plans/courses/${config.courseId}/${selfPacedTabs[0].route}/${createdPlanIdParamString}`
            );
        }

        /** Clear plan cards and topic cards that are checked in other tabs **/
        selectedPlanIds.set(new Set([]));
        selectedTopicIds.set(new Set([]));
    }

    function addCountsToTabTitles() {
        selfPacedTabs[0].title =
            allCounts.topicsCount >= maxCountTreshold
                ? `${config.topicName} ${maxCountDisplayText}`
                : `${config.topicName} (${allCounts.topicsCount})`;
        selfPacedTabs[1].title =
            allCounts.allPlansCount >= maxCountTreshold
                ? `${i18n.allPlans} ${maxCountDisplayText}`
                : `${i18n.allPlans} (${allCounts.allPlansCount})`;
    }

    async function createNewPlan(event) {
        event.preventDefault();

        const planParams = {
            topic: null,
            start: "0000-00-00 00:00:00",
            stop: "0000-00-00 00:00:00"
        };

        const newPlan = config.assignToEnabled ? await createPlanV2(planParams) : await createPlan(planParams);

        createNewPlanUrl = addBackDestinationParam(
            `${config.newPlanUrl}/${newPlan.planId}?pendingPlanId=${newPlan.planId}`
        );

        window.location.href = createNewPlanUrl;

        return false;
    }

    async function afterActionSelfPaced() {
        resetLoadStatus();
        loadAllPlanCounts();

        switch (selectedTab) {
            case plannerSelfPacedTabIds.all:
            case plannerTabIds.all:
                await loadAllPlans(config.publicAccessKey);
                break;
            case plannerSelfPacedTabIds.topics:
            case plannerTabIds.topics:
                loadTopics(config.publicAccessKey);
                break;
            default:
                break;
        }
    }

    function popState({ state }) {
        if (state && state.tab !== undefined) {
            selectedTab = state.tab;
            lastSelectedSelfPacedTab = state.tab;
        }
    }

    function resetLoadStatus() {
        hasSelfPacedPlansLoaded = false;
    }

    function resetLoadStatusOfSelectedTab() {
        if (
            lastSelectedSelfPacedTab === plannerSelfPacedTabIds.all ||
            lastSelectedSelfPacedTab === plannerTabIds.all
        ) {
            hasSelfPacedPlansLoaded = false;
        }
    }

    function tabChangedNotification() {
        let tab;

        if (selectedTab === plannerSelfPacedTabIds.all || selectedTab === plannerTabIds.all) {
            tab = plannerTabIds.all;
        } else if (
            selectedTab === plannerSelfPacedTabIds.topics || selectedTab === plannerTabIds.topics) {
            tab = plannerTabIds.topics;
        }

        dispatch("tabChanged", { tab });
    }
</script>
